import React from "react";
import { Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import BigHeader from "../../components/Elements/BigHeader";
import PrimaryButton from "../../components/Elements/PrimaryButton";
import SmallDetail from "../../components/Elements/SmallDetail";
import * as Constants from "../../features/generalConstants";
import Location from "../../features/rapidstor/entities/Location";
import store from "../../store";
import { selectUnitData, setContactData } from "../../store/bookingSlice";
import classes from "./BookingForm.module.scss";
import { useCookies } from "react-cookie";
import { createId } from "@paralleldrive/cuid2";
import {
  RapidStorTrackEvent,
  RapidStorTrackEventRequest,
  options,
} from "../../services/rapidstor";
import { usePostTrackEventMutation } from "../../features/rapidstor/rapidstorApiSlice";
import { Logger } from "../../main";

function BookingForm() {
  const unitData = useSelector(selectUnitData);
  const navigate = useNavigate();
  const location = useLoaderData() as Location;
  const [, setCookie] = useCookies(["user"]);
  const [postTrackEvent] = usePostTrackEventMutation();

  React.useEffect(() => {
    if (!unitData) {
      navigate(`/${location.slug}`);
    }
  }, [location.slug, navigate, unitData]);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      addr1: "",
      addr2: "",
      city: "",
      state: "",
      postalCode: "",
      declaredInsuranceAmt: 0,
      locationCode: location.locationCode,
    };
    setCookie(
      "user",
      {
        ...formData,
        descriptor: unitData?.selectedDescriptor.area,
      },
      {
        // Dirname of the current location.
        path: window.location.pathname.split("/").slice(0, -1).join("/"),
        maxAge: 60 * 60 * 24 * 7,
      }
    );
    store.dispatch(setContactData(formData));
    const rapidStorTrackEventPayload: RapidStorTrackEvent = {
      trackingId: createId(),
      url: window.location.href,
      referrerUrl: document.referrer,
      userAgent: window.navigator.userAgent,
      action: "email_user_quoted",
      timestamp: new Date().toISOString(),
    };
    const rapidStorTrackEvent: RapidStorTrackEventRequest = {
      locationToken: location.token,
      corpToken: options.corpToken,
      payload: rapidStorTrackEventPayload,
    };
    postTrackEvent(rapidStorTrackEvent);
    if (window.rapidstor_api_tracking_listener) {
      try {
        window.rapidstor_api_tracking_listener("show-prices", {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          neededDate: "",
          description: unitData?.selectedDescriptor.name,
          tier: 0,
          locationCode: location.locationCode,
          unitTypeId: unitData?.unitType?._id ?? "",
        });
      } catch (e) {
        Logger().error(JSON.stringify(e));
      }
    }
    navigate(`/${location.slug}/quote`);
  }

  const [form, updateForm] = useImmer<Record<string, string>>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  if (!unitData) {
    return null;
  }

  function update(key: string, event: React.BaseSyntheticEvent) {
    updateForm((draft) => {
      draft[key] = String(event.target.value);
      return draft;
    });
  }

  return (
    <div>
      <Row>
        <div>
          <div className={classes.header}>
            <BigHeader>{Constants.CONTACT_FORM.HEADER_TEXT}</BigHeader>
            <SmallDetail>
              One step closer to your{" "}
              <strong>{unitData.selectedDescriptor.area} sqft</strong> unit at{" "}
              {unitData.location.name}.
            </SmallDetail>
          </div>
        </div>

        <Form className={`${classes.form}`} onSubmit={submit}>
          <label>First Name</label>
          <Form.Control
            type={"text"}
            placeholder={"First Name"}
            name="firstName"
            autoComplete="given-name"
            required
            value={form.firstName}
            onChange={(e) => update("firstName", e)}
          />
          <label>Last Name</label>
          <Form.Control
            type={"text"}
            placeholder={"Last Name"}
            name="lastName"
            autoComplete="family-name"
            required
            value={form.lastName}
            onChange={(e) => update("lastName", e)}
          />
          <label>Phone Number</label>
          <Form.Control
            type="tel"
            placeholder={"Phone Number"}
            name="phone"
            pattern="[0-9\s]{10,}"
            autoComplete="tel-national"
            required
            value={form.phone}
            onChange={(e) => update("phone", e)}
          />
          <label>Email</label>
          <Form.Control
            type={"email"}
            placeholder={"Email"}
            name="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(e) => update("email", e)}
          />
          <div className={classes.quote_button}>
            <PrimaryButton submit responsive>
              {Constants.CONTACT_FORM.BUTTON_TEXT}
            </PrimaryButton>
          </div>
        </Form>
      </Row>
    </div>
  );
}
export default BookingForm;
